/**
 * shared/src/schemas/auth.schema.ts
 *
 * FONTE UNICA del contratto utente (client ↔ server).
 *
 * Storia (Ott 2026): esistevano TRE definizioni diverse dello stesso oggetto —
 * qui, in `server/src/db/schema.ts` (mai importato da nessuno) e in
 * `client/src/types/user.types.ts` — con valori di `user_type` incompatibili
 * ('rider' vs 'private' vs 'biker'). Il risultato era che lo schema condiviso
 * non descriveva nulla di reale e nessuno poteva usarlo per validare.
 *
 * Qui i valori sono quelli che il codice usa davvero:
 *   - `user_type`: biker | dealer | admin | chapter_admin | private
 *   - `subscription_tier`: stringa libera (rider_free, rider_plus, rider_premium,
 *     dealer_free, dealer_pro, …): i piani cambiano spesso, un enum qui
 *     significherebbe una migrazione dello schema a ogni nuovo piano.
 *
 * Il test `server/src/__tests__/sharedUserSchemaContract.test.ts` confronta
 * questo schema con l'interfaccia `User` del client e con i campi reali
 * restituiti da `GET /api/auth/me`: se un lato cambia senza l'altro, la suite
 * diventa rossa.
 */
import { z } from 'zod';
/** Tipi di account realmente presenti in `users.user_type`. */
export const UserTypeSchema = z.enum([
    'biker',
    'dealer',
    'admin',
    'chapter_admin',
    'private',
]);
/**
 * Utente come lo restituisce l'API (login, refresh, GET /api/auth/me).
 * I campi credenziale (password_hash, mfa_secret, mfa_setup_at) NON fanno
 * parte del contratto: se compaiono nella risposta è un bug di sicurezza
 * (vedi `server/src/utils/publicUser.ts`).
 */
export const UserSchema = z
    .object({
    id: z.string().uuid(),
    email: z.string().email(),
    user_type: UserTypeSchema,
    avatar_url: z.string().nullable().optional(),
    is_active: z.boolean().optional(),
    created_at: z.string().optional(),
    username: z.string().nullable().optional(),
    nickname: z.string().nullable().optional(),
    first_name: z.string().nullable().optional(),
    last_name: z.string().nullable().optional(),
    phone: z.string().nullable().optional(),
    codice_fiscale: z.string().nullable().optional(),
    address_street: z.string().nullable().optional(),
    address_civic: z.string().nullable().optional(),
    address_cap: z.string().nullable().optional(),
    address_city: z.string().nullable().optional(),
    address_province: z.string().nullable().optional(),
    profile_completed: z.boolean().optional(),
    total_km: z.number().optional(),
    subscription_tier: z.string().nullable().optional(),
    trial_used: z.boolean().nullable().optional(),
    trial_ends_at: z.string().nullable().optional(),
    is_premium: z.boolean().optional(),
    premium_until: z.number().nullable().optional(),
    rider_plan_code: z.string().nullable().optional(),
    plan_code: z.string().nullable().optional(),
    chapter_admin_of: z.string().nullable().optional(),
    is_agent: z.boolean().optional(),
    agent_affiliate_code: z.string().nullable().optional(),
    is_locale: z.boolean().optional(),
    mfa_enabled: z.boolean().optional(),
})
    .passthrough();
export const LoginInputSchema = z.object({
    email: z.string().email(),
    password: z.string().min(6).max(128),
});
export const RegisterInputSchema = z.object({
    email: z.string().email(),
    password: z.string().min(8).max(128),
    nickname: z.string().min(2).max(30).optional(),
    user_type: z.enum(['biker', 'dealer']).optional().default('biker'),
});
export const AuthResponseSchema = z.object({
    accessToken: z.string(),
    user: UserSchema,
});
//# sourceMappingURL=auth.schema.js.map